import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  ApiError,
  can,
  effectiveRole,
  hasCapability,
  isBaygon,
  isChefUniteRole,
  syncWebPushSubscription,
  type AccessLevel,
  type Capability,
  type Resource,
  type Role,
  type MfaStatus,
  type StaffIdentity,
  type User,
} from '@bala/core';
import { api } from './api';

/**
 * Session de l'animateur connecté. Les permissions sont exposées sous forme de
 * prédicats afin que les écrans n'aient jamais à raisonner sur la structure du
 * rôle — et que la même règle serve à masquer un bouton ici et à refuser
 * l'action côté serveur.
 */
export interface AuthState {
  status: 'loading' | 'anonymous' | 'authenticated';
  user: User | null;
  role: Role | null;
  /** `true` si l'installation n'a pas encore de compte animateur. */
  needsBootstrap: boolean;
  can: (resource: Resource, level?: AccessLevel) => boolean;
  hasCapability: (capability: Capability) => boolean;
  isBaygon: boolean;
  /** Chef d'Unité : consultation seule, toute action d'écriture est masquée. */
  isChefUnite: boolean;
  /** Compte testeur : permissions granulaires, MDP imposé, accès aussi à Parents. */
  signIn: (email: string, password: string) => Promise<{ challenge?: string } | void>;
  /** Second temps de la connexion, quand la double authentification est active. */
  verifyCode: (challenge: string, code: string) => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
  /** État de la double authentification du compte connecté. */
  mfa: MfaStatus | null;
}

const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth doit être utilisé dans un <AuthProvider>.');
  return context;
}

/**
 * Réponse de `/auth/login` : soit la session est ouverte, soit le compte
 * demande un second facteur et renvoie un jeton de défi valable cinq minutes.
 */
interface SessionResponse {
  expiresAt?: string;
  audienceType: 'user' | 'parent';
  mfaRequired?: boolean;
  challenge?: string;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthState['status']>('loading');
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<Role | null>(null);
  const [mfa, setMfa] = useState<MfaStatus | null>(null);
  const [needsBootstrap, setNeedsBootstrap] = useState(false);

  const loadIdentity = useCallback(async () => {
    // La session vit dans un cookie `HttpOnly` : impossible de savoir d'ici si
    // elle existe. On demande donc toujours l'identité, et un 401 signifie
    // « anonyme ». Un aller-retour de plus, pour les visiteurs non connectés
    // seulement.
    try {
      const identity = await api.get<StaffIdentity>('/auth/me', { cache: false });
      if (identity.audienceType !== 'user') {
        // Une session parent sur l'app Staff : on la rejette explicitement
        // plutôt que d'afficher une interface vide.
        await api.signOut();
        setStatus('anonymous');
        return;
      }
      api.setSession(true);
      setUser(identity.user);
      setRole(identity.role);
      setMfa(identity.mfa ?? null);
      setStatus('authenticated');
      void api.flushOutbox();
      // Réenregistre le Push si la permission est déjà accordée (nouvel appareil
      // ou SW rafraîchi) — sans redemander la permission.
      void syncWebPushSubscription(api);
    } catch (error) {
      // Une session expirée ou révoquée ramène à l'écran de connexion ; une
      // simple coupure réseau ne doit pas déconnecter l'animateur.
      if (error instanceof ApiError && error.status === 401) {
        // Sans session, on vérifie si l'installation attend son premier Baygon.
        try {
          const info = await api.get<{ initialized: boolean }>('/auth/status', { cache: false });
          setNeedsBootstrap(!info.initialized);
        } catch {
          setNeedsBootstrap(false);
        }
      }
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        await api.signOut();
      }
      setStatus('anonymous');
    }
  }, []);

  useEffect(() => {
    // Récupère une session ouverte avant le passage au cookie, avant même de
    // demander l'identité. À retirer avec le reste de la passerelle.
    void api.adoptLegacyToken().then(loadIdentity);
  }, [loadIdentity]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const session = await api.post<SessionResponse>(
        '/auth/login',
        { email, password, audience: 'user' },
        { queueOffline: false },
      );
      if (session.audienceType !== 'user') {
        throw new ApiError(403, "Ce compte est un compte parent : utilisez l'application Parents.");
      }
      // Double authentification : rien n'est encore ouvert, l'écran de
      // connexion enchaîne sur la saisie du code.
      if (session.mfaRequired && session.challenge) return { challenge: session.challenge };
      await loadIdentity();
      return undefined;
    },
    [loadIdentity],
  );

  const verifyCode = useCallback(
    async (challenge: string, code: string) => {
      await api.post('/auth/mfa/verify', { challenge, code }, { queueOffline: false });
      await loadIdentity();
    },
    [loadIdentity],
  );

  const signOut = useCallback(async () => {
    try {
      await api.post('/auth/logout', undefined, { queueOffline: false });
    } catch {
      // Même si l'appel échoue (hors connexion), on efface la session locale.
    }
    await api.signOut();
    setUser(null);
    setRole(null);
    setMfa(null);
    setStatus('anonymous');
  }, []);

  const value = useMemo<AuthState>(() => {
    const effective = role ? effectiveRole(role) : null;
    return {
      status,
      user,
      role,
      needsBootstrap,
      can: (resource, level = 'read') => (effective ? can(effective, resource, level) : false),
      hasCapability: (capability) => (effective ? hasCapability(effective, capability) : false),
      isBaygon: effective ? isBaygon(effective) : false,
      isChefUnite: isChefUniteRole(role),
      signIn,
      verifyCode,
      signOut,
      refresh: loadIdentity,
      mfa,
    };
  }, [status, user, role, mfa, needsBootstrap, signIn, verifyCode, signOut, loadIdentity]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Masque son contenu si l'utilisateur n'a pas le niveau requis. Évite de
 * répéter `auth.can(...) && <…>` dans chaque écran.
 */
export function Gate({
  resource,
  level = 'read',
  capability,
  fallback = null,
  children,
}: {
  resource?: Resource;
  level?: AccessLevel;
  capability?: Capability;
  fallback?: ReactNode;
  children: ReactNode;
}) {
  const auth = useAuth();
  if (resource && !auth.can(resource, level)) return <>{fallback}</>;
  if (capability && !auth.hasCapability(capability)) return <>{fallback}</>;
  return <>{children}</>;
}
